/**
 * marquee.js (standalone Shopify-safe)
 * - No imports
 * - Duplicates content for infinite loop
 * - Recalculates on resize
 * - Optional: disables/hides image marquees on older Safari
 */

(() => {
  class MarqueeComponent extends HTMLElement {
    connectedCallback() {
      // Prevent double init
      if (this.__marqueeInit) return;
      this.__marqueeInit = true;

      this.wrapper = this.querySelector('[ref="wrapper"]');
      this.content = this.querySelector('[ref="content"]');

      if (!this.wrapper || !this.content) return;

      // Some themes render refs like marqueeItems[] on children
      this.items = Array.from(this.querySelectorAll('[ref="marqueeItems[]"]'));
      if (!this.items.length) return;

      // Respect reduced motion
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setAttribute('data-disabled', '');
        return;
      }

      // Optional: tag whether this marquee contains images (for fallbacks)
      const hasImages = !!this.querySelector('img, picture, svg');
      if (hasImages) this.setAttribute('data-has-images', '');

      // Optional fallback: older Safari can be quirky with image marquees.
      // If you want to hide ONLY image marquees on Safari <= 15.x, enable this:
      if (hasImages && isSafari() && getSafariMajorVersion() && getSafariMajorVersion() <= 15) {
        this.setAttribute('data-disabled', '');
        this.setAttribute('data-hidden-fallback', '');
        this.style.display = 'none';
        return;
      }

      // Ensure structure supports looping
      this.__setup();

      // Resize handling
      this.__onResize = debounce(() => {
        this.__teardownClones();
        this.__setup();
        this.__restartAnimation();
      }, 200);

      window.addEventListener('resize', this.__onResize);

      // Optional hover slow-down on pointer devices
      this.__onEnter = () => this.style.setProperty('--marquee-rate', '0');
      this.__onLeave = () => this.style.setProperty('--marquee-rate', '1');

      this.addEventListener('pointerenter', this.__onEnter);
      this.addEventListener('pointerleave', this.__onLeave);

      // Mark ready so CSS can safely animate only after cloning
      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.__onResize);
      this.removeEventListener('pointerenter', this.__onEnter);
      this.removeEventListener('pointerleave', this.__onLeave);
    }

    __setup() {
      // 1) Find the "base" repeated-items node (the first one)
      const baseItem = this.items[0];
      if (!baseItem) return;

      // 2) Make enough repeated items so that one "content" block is at least as wide as the viewport
      const viewportWidth = this.getBoundingClientRect().width || 0;

      // Temporarily remove any previous clones in content (safety)
      this.__removeExtraRepeatedItems();

      let contentWidth = this.content.scrollWidth || 0;
      let safety = 0;

      // Add repeated items until we cover the viewport (plus a little buffer)
      while (contentWidth < viewportWidth * 1.2 && safety < 30) {
        const clone = baseItem.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('ref');
        this.content.appendChild(clone);
        contentWidth = this.content.scrollWidth || 0;
        safety++;
      }

      // 3) Duplicate the whole content once (this enables the -50% translate loop)
      this.__duplicateContentBlock();

      // 4) Set speed based on how many repeated items exist
      const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;
      const copies = Math.max(1, this.content.children.length);
      const seconds = Math.max(8, Math.sqrt(copies) * speedFactor);

      this.style.setProperty('--marquee-speed', `${seconds}s`);

      // Default playback rate (used by optional hover slow-down)
      this.style.setProperty('--marquee-rate', '1');
    }

    __removeExtraRepeatedItems() {
      // Keep the first repeated-items group and remove any additional groups INSIDE content
      // BUT do not remove the second duplicated content block (handled separately)
      const children = Array.from(this.content.children);
      // If there are already multiple repeated-items groups (from previous run), keep first, remove rest
      for (let i = 1; i < children.length; i++) {
        children[i].remove();
      }
    }

    __duplicateContentBlock() {
      // Remove existing cloned content block if present
      const last = this.wrapper.lastElementChild;
      if (last && last !== this.content && last.hasAttribute('data-marquee-clone')) {
        last.remove();
      }

      const clone = this.content.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-marquee-clone', 'true');
      clone.removeAttribute('ref');

      this.wrapper.appendChild(clone);
    }

    __teardownClones() {
      // Remove cloned wrapper child
      const last = this.wrapper.lastElementChild;
      if (last && last !== this.content && last.hasAttribute('data-marquee-clone')) {
        last.remove();
      }

      // Reset content to only its first child (the original repeated-items group)
      const kids = Array.from(this.content.children);
      for (let i = 1; i < kids.length; i++) kids[i].remove();

      // Refresh refs list
      this.items = Array.from(this.querySelectorAll('[ref="marqueeItems[]"]'));
    }

    __restartAnimation() {
      // Force reflow to restart CSS animation reliably (esp Safari)
      this.wrapper.style.animation = 'none';
      // eslint-disable-next-line no-unused-expressions
      this.wrapper.offsetHeight;
      this.wrapper.style.animation = '';
    }
  }

  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  function isSafari() {
    const ua = navigator.userAgent;
    const isChrome = /Chrome|Chromium|CriOS/.test(ua);
    const isAndroid = /Android/.test(ua);
    return /Safari/.test(ua) && !isChrome && !isAndroid;
  }

  function getSafariMajorVersion() {
    // Safari UA often includes "Version/17.6" etc
    const m = navigator.userAgent.match(/Version\/(\d+)\./);
    return m ? parseInt(m[1], 10) : null;
  }

  if (!customElements.get('marquee-component')) {
    customElements.define('marquee-component', MarqueeComponent);
  }
})();
